import React, { useState, useEffect } from 'react';
import { Clock, Shield, AlertTriangle, CheckCircle, RefreshCw, Pause } from 'lucide-react';
import styles from '../CSS/DeviceStatusPanel.module.css';

const DeviceStatusPanel = ({ deviceData, devices = [], deviceNames = {} }) => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (deviceData) {
      console.log("DeviceStatusPanel - Datos recibidos del padre:", deviceData);
    }
  }, [deviceData]);

  if (!deviceData || Object.keys(deviceData).length === 0) {
    return null;
  }

  const totalDevices = Object.keys(deviceData).length;

  const groupNames = {
    '266': 'YouTube',
    '133': 'Safari',
  };

  const allGroupIds = ['266', '133'];

  const devicesByGroup = {};
  const pendingRestrictions = {};

  Object.entries(deviceData).forEach(([udid, device]) => {
    const deviceInfo = devices.find(d => d.UDID === udid);
    const rawName = deviceNames[udid] || deviceInfo?.name || device.name || udid;
    const displayName = rawName.replace(/\biPad\b/gi, '').trim() || 'Sin Asignar';

    allGroupIds.forEach(groupId => {
      const groupRestriction = device.groupRestrictions?.[groupId];

      if (groupRestriction?.status === 'blocked') {
        if (!devicesByGroup[groupId]) {
          devicesByGroup[groupId] = [];
        }
        devicesByGroup[groupId].push({
          udid,
          name: displayName,
          expirationTime: groupRestriction.expirationTime
        });
      }

      if (device.previousRestrictions && device.previousRestrictions[groupId]) {
        const restrictionData = device.previousRestrictions[groupId];
        if (!pendingRestrictions[groupId]) {
          pendingRestrictions[groupId] = [];
        }
        pendingRestrictions[groupId].push({
          udid,
          name: displayName,
          ...restrictionData
        });
      }
    });
  });

  const uniqueBlockedDevices = new Set();
  const uniquePausedDevices = new Set();

  Object.values(deviceData).forEach(data => {
    const hasBlockedGroup = Object.values(data.groupRestrictions || {}).some(
      r => r.status === 'blocked'
    );
    if (hasBlockedGroup) uniqueBlockedDevices.add(data.id);

    const hasPausedGroup = Object.values(data.groupRestrictions || {}).some(
      r => r.status === 'paused'
    );
    if (hasPausedGroup) uniquePausedDevices.add(data.id);
  });

  const blockedDevices = uniqueBlockedDevices.size;
  const pausedDevices = uniquePausedDevices.size;
  const allowedDevices = totalDevices - blockedDevices - pausedDevices;

  const formatTimeRemaining = (expirationTime, isPending = false, pauseInfo = null) => {
    if (!expirationTime) return 'Permanente';
    const now = time;
    const targetTime = expirationTime?.toDate ? expirationTime.toDate() : new Date(expirationTime);

    if (pauseInfo) {
      let adjustedRemainingTime = pauseInfo.remainingTime || 0;
      if (isPending) {
        const resumeAfter = pauseInfo.resumeAfter?.toDate ? pauseInfo.resumeAfter.toDate() : new Date(pauseInfo.resumeAfter);
        if (resumeAfter) {
          adjustedRemainingTime = resumeAfter.getTime() - now.getTime();
        }
      }
      if (adjustedRemainingTime <= 0) return isPending ? 'Reanudando...' : 'Expirado';
      return formatDuration(adjustedRemainingTime);
    }

    const diff = targetTime - now;
    if (diff <= 0) return 'Expirado';
    return formatDuration(diff);
  };

  const formatDuration = (milliseconds) => {
    if (milliseconds < 0) return '0s';
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  return (
    <div className={styles.statusPanel}>
      <div className={styles.summary}>
        <div className={styles.statusCard}>
          <h4>Resumen de dispositivos</h4>
          <div className={styles.statusRow}>
            <Shield className={styles.icon} />
            <span>Total: {totalDevices}</span>
          </div>
          <div className={styles.statusRow}>
            <CheckCircle className={`${styles.icon} ${styles.allowed}`} />
            <span>{allowedDevices} permitidos</span>
          </div>
          <div className={styles.statusRow}>
            <AlertTriangle className={`${styles.icon} ${styles.blocked}`} />
            <span>{blockedDevices} bloqueados</span>
          </div>
          {pausedDevices > 0 && (
            <div className={styles.statusRow}>
              <Pause className={`${styles.icon} ${styles.paused}`} />
              <span>{pausedDevices} pausados</span>
            </div>
          )}
        </div>
      </div>

      {Object.keys(devicesByGroup).length > 0 && (
        <div className={styles.restrictionsList}>
          <div className={styles.restrictionsHeader}>
            <h4>Restricciones activas</h4>
          </div>
          {Object.entries(devicesByGroup).map(([groupId, devices]) => (
            <div key={groupId} className={styles.restrictionGroup}>
              <div className={styles.groupHeader}>
                <span className={styles.groupName}>{groupNames[groupId] || `Grupo ${groupId}`}</span>
                <span className={styles.deviceCount}>{devices.length} dispositivo(s)</span>
              </div>
              <div className={styles.devicesList}>
                {devices.map((device, idx) => (
                  <div key={`${device.udid}-${idx}`} className={styles.deviceItem}>
                    <div className={styles.deviceId}>{device.name}</div>
                    <div className={styles.expirationTime}>
                      <Clock size={14} />
                      <span>{formatTimeRemaining(device.expirationTime)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {Object.keys(pendingRestrictions).length > 0 && (
        <div className={styles.pendingRestrictionsList}>
          <div className={styles.restrictionsHeader}>
            <h4>Restricciones pendientes de reanudar</h4>
          </div>
          {Object.entries(pendingRestrictions).map(([groupId, devices]) => (
            <div key={groupId} className={styles.pendingRestrictionGroup}>
              <div className={styles.groupHeader}>
                <span className={styles.groupName}>{groupNames[groupId] || `Grupo ${groupId}`}</span>
                <span className={styles.deviceCount}>{devices.length} dispositivo(s)</span>
              </div>
              <div className={styles.devicesList}>
                {devices.map((device, idx) => (
                  <div key={`${device.udid}-${idx}`} className={styles.deviceItem}>
                    <div className={styles.deviceId}>{device.name}</div>
                    <div className={styles.pendingExpirationTime}>
                      <Pause size={14} />
                      <span>Reanuda en {formatTimeRemaining(device.resumeAfter, true, device)}</span>
                      <span className={styles.remainingTime}>
                        (Bloqueo restante: {device.remainingTime ? formatDuration(device.remainingTime) : 'Permanente'})
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DeviceStatusPanel;